import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../constants/environment';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import { QuotingSettings, SelectionSettings, OrderingSettings, OrgVendor, InsuranceDrpSettings, OEMSettings, RekeySettings, PriceMatchSettings, InvoicingSettingsUpdatePayload, OrgInventorySettings } from './useGetSettings';

export interface UpdatedSettings {
    activityContext?: {
        invoiceId?: string;
    };
    quoting?: QuotingSettings;
    selection?: SelectionSettings;
    ordering?: OrderingSettings;
    vendors?: Array<OrgVendor>;
    insurance?: Array<InsuranceDrpSettings>;
    oem?: Array<OEMSettings>;
    rekeySettings?: RekeySettings;
    priceMatching?: PriceMatchSettings;
    invoicingSettings?: InvoicingSettingsUpdatePayload;
    /** Receive opt-in toggle — the server stamps delivery.receivingEnabledAt. */
    delivery?: { receivingEnabled: boolean; };
    /** NEX purchase-order opt-in (Beta Features): org switch + per-location allow-list. */
    nexOrdering?: { enabled: boolean; locationIds: string[]; };
    inventory?: OrgInventorySettings;
}

const updateSettings = async (updatedSettings: UpdatedSettings, sessionId: string | null) => {
    return axios.post(`${API_URL}/settings/update`,
        updatedSettings,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
};

export const useUpdateSettings = (options?: UseMutationOptions<any, Error, UpdatedSettings>) => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (updatedSettings: UpdatedSettings) => updateSettings(updatedSettings, sessionContext?.sessionId ?? null),
        ...options,
        onSuccess: (...args) => {
            const variables = args[1] as UpdatedSettings;
            if (variables.invoicingSettings) {
                queryClient.invalidateQueries({
                    queryKey: ['/invoice/activity-logs', 'all'],
                });
                if (variables.activityContext?.invoiceId) {
                    queryClient.invalidateQueries({
                        queryKey: ['/invoice/activity-logs', variables.activityContext.invoiceId],
                    });
                }
            }
        },
    });
};